/**
 * Markdown catalog used only when Surreal is empty or unreachable.
 * Live listings query Surreal first.
 */

import blogManifest from '@/content/blog-manifest.json';
import {
  formatArticleDate,
  presentedDate,
  type ArticleEdit,
} from '@/lib/articleEdits';

export type ManifestBlogPost = {
  slug: string;
  title: string;
  author?: string;
  authorImage?: string | null;
  headerImage?: string | null;
  description?: string;
  date?: string;
  edits?: ArticleEdit[];
  readTime?: string;
  tags?: string[];
  featured?: boolean;
  url: string;
};

export type ListedBlogPost = {
  slug: string;
  title: string;
  author?: string;
  authorImage?: string;
  headerImage?: string;
  description?: string;
  date?: string;
  updated?: string;
  readTime?: string;
  tags: string[];
  featured: boolean;
  url: string;
  dateTimestamp: number;
  formattedDate: string;
};

function presentedTimestamp(shown?: string): number {
  if (!shown) return 0;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(shown);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
  }
  const parsed = new Date(shown);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

export function postsFromManifest(): ListedBlogPost[] {
  return (blogManifest.posts as ManifestBlogPost[]).map((post) => {
    const shown = presentedDate(post.date, post.edits) ?? post.date;
    return {
      slug: post.slug,
      title: post.title,
      author: post.author,
      authorImage: post.authorImage ?? undefined,
      headerImage: post.headerImage ?? undefined,
      description: post.description,
      date: post.date,
      updated: shown,
      readTime: post.readTime,
      tags: post.tags ?? [],
      featured: Boolean(post.featured),
      url: post.url,
      dateTimestamp: presentedTimestamp(shown),
      formattedDate: shown ? formatArticleDate(shown) : '',
    };
  }).sort((a, b) => b.dateTimestamp - a.dateTimestamp);
}

export function manifestPostBySlug(): Map<string, ListedBlogPost> {
  return new Map(postsFromManifest().map((post) => [post.slug, post]));
}

export function overlayManifestPost<T extends { slug: string }>(
  row: T,
): T & ListedBlogPost {
  const listed = manifestPostBySlug().get(row.slug);
  if (!listed) {
    return row as T & ListedBlogPost;
  }
  return { ...row, ...listed };
}
